import { useState } from 'react';
import { useRouter } from 'next/router';

import { useRentalManagerUserPropertyMultiEditWebApiHandler_multi } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useApplyListingDetailsPatches } from './queries/listingDetailsCache';
import { usePropertyEditMutation } from './usePropertyEditMutation';

import type { Client } from '@zg-rentals/http-client';
import type {
  UserPropertyMultiEditCentrarFieldErrorWebDto,
  UserPropertyMultiEditResponseWebDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import type { RentalManagerUserPropertyMultiEditWebApiHandler_multi_Args } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import type { ListingDetailsPatch } from './queries/listingDetailsCache';

export class MultiEditSoftError extends Error {
  readonly fieldErrors: Array<UserPropertyMultiEditCentrarFieldErrorWebDto>;

  constructor(fieldErrors: Array<UserPropertyMultiEditCentrarFieldErrorWebDto>) {
    super('Multi-edit soft error');
    this.name = 'MultiEditSoftError';
    this.fieldErrors = fieldErrors;
  }

  static is(error: unknown): error is MultiEditSoftError {
    return error instanceof MultiEditSoftError;
  }

  getMessageForField(apiFieldName: string): string | null {
    return this.fieldErrors.find((e) => e.fieldName === apiFieldName)?.message ?? null;
  }
}

type UseMultiEditMutationOptions = {
  onSuccess?: (data: UserPropertyMultiEditResponseWebDto) => void;
  onError?: (error: unknown) => void;
  successToastMessage?: string;
  skipToast?: boolean;
  skipTracking?: boolean;
  meta?: { invalidates?: Array<string>; context?: Record<string, unknown> };
  httpClient?: Client;
  /** Held here, not passed down, so the soft-error check below can drop rejected fields first. */
  applyToCache?: (
    variables: RentalManagerUserPropertyMultiEditWebApiHandler_multi_Args,
    data: UserPropertyMultiEditResponseWebDto,
  ) => Array<ListingDetailsPatch>;
};

const extractSoftErrors = (data: UserPropertyMultiEditResponseWebDto): MultiEditSoftError | null => {
  const propertyErrors = data.property?.success === false ? (data.property.errors ?? []) : [];
  const floorPlanErrors = (data.floorPlans ?? []).filter((fp) => fp.success === false).flatMap((fp) => fp.errors ?? []);
  const allErrors = [...propertyErrors, ...floorPlanErrors];
  return allErrors.length > 0 ? new MultiEditSoftError(allErrors) : null;
};

function collectLeafKeys(record: Record<string, unknown>, skipKey: string, fields: Set<string>) {
  for (const [key, value] of Object.entries(record)) {
    if (key === skipKey) continue;
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      Object.keys(value as Record<string, unknown>).forEach((k) => fields.add(k));
    } else {
      fields.add(key);
    }
  }
}

// Extract field names from mutation variables to know which accumulated errors
// to clear on a successful save.
function extractSavedFields(variables: unknown): Set<string> {
  const postData = (
    variables as { postData?: { property?: Record<string, unknown>; floorPlans?: Array<Record<string, unknown>> } }
  )?.postData;
  if (!postData) return new Set();

  const fields = new Set<string>();
  if (postData.property) collectLeafKeys(postData.property, 'listingAlias', fields);
  for (const floorPlan of postData.floorPlans ?? []) collectLeafKeys(floorPlan, 'id', fields);
  return fields;
}

export const useMultiEditMutation = ({ applyToCache, ...options }: UseMultiEditMutationOptions = {}) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const applyListingDetailsPatches = useApplyListingDetailsPatches();

  const mutation = usePropertyEditMutation({
    ...options,
    mutationHook: useRentalManagerUserPropertyMultiEditWebApiHandler_multi,
  });

  // Accumulated per-field errors across multiple saves. Each save clears the
  // errors for the fields it touched, then merges in any new errors from the
  // response. This prevents a later save from wiping errors from an earlier
  // save on a different field.
  const [accumulatedFieldErrors, setAccumulatedFieldErrors] = useState<
    Array<UserPropertyMultiEditCentrarFieldErrorWebDto>
  >([]);

  const wrappedMutateAsync: typeof mutation.mutateAsync = async (variables, mutateOptions) => {
    const savedFields = extractSavedFields(variables);

    const data = await mutation.mutateAsync(variables, mutateOptions);
    const callError = extractSoftErrors(data);

    setAccumulatedFieldErrors((prev) => {
      const remaining = prev.filter((e) => !savedFields.has(e.fieldName));
      return callError ? [...remaining, ...callError.fieldErrors] : remaining;
    });

    // Before the throw, so a partial success still writes the fields it saved.
    if (applyToCache) {
      const rejectedFields = new Set(callError?.fieldErrors.map((e) => e.fieldName));
      applyListingDetailsPatches(
        listingAlias,
        applyToCache(variables as RentalManagerUserPropertyMultiEditWebApiHandler_multi_Args, data).filter(
          (patch) => !rejectedFields.has(patch.field),
        ),
      );
    }

    if (callError) throw callError;
    return data;
  };

  const accumulatedSoftError =
    accumulatedFieldErrors.length > 0 ? new MultiEditSoftError(accumulatedFieldErrors) : null;

  return {
    ...mutation,
    error: accumulatedSoftError ?? mutation.error,
    mutateAsync: wrappedMutateAsync,
  } as typeof mutation;
};
